import { useState, useCallback } from 'react';
import airbrake from 'common/airbrake';
import { defaultHeaders, checkStatus } from 'common/http';
import { fetchApprovalsGuidanceV2 } from 'common/core/approvals';
import { coreUidFromAssetId } from 'common/cetera/catalog_id';
import { View } from 'common/types/view';
import { GuidanceSummaryV2 } from 'common/types/approvals';

interface UseActionDropdownDataProps {
  uid: string;
  isCatalogFederatedView?: boolean;
  isDataFederatedView?: boolean;
}

interface ActionDropdownData {
  fetchedApprovalsGuidance: GuidanceSummaryV2 | null;
  failedViewFetch: boolean;
  fetchingViewAndGuidance: boolean;
  viewFetched: boolean;
  view?: View;
}

/**
 * Hook to manage fetching view and approvals guidance data for the action dropdown
 */
export function useActionDropdownData({
  uid,
  isCatalogFederatedView,
  isDataFederatedView,
}: UseActionDropdownDataProps) {
  const [data, setData] = useState<ActionDropdownData>({
    fetchedApprovalsGuidance: null,
    failedViewFetch: false,
    fetchingViewAndGuidance: false,
    viewFetched: false,
  });

  const fetchViewAndGuidance = useCallback(async () => {
    const fourByFour = coreUidFromAssetId(uid);
    const fetchOptions = {
      method: 'GET',
      credentials: 'same-origin' as RequestCredentials,
      headers: defaultHeaders,
    };

    setData((prev) => ({ ...prev, fetchingViewAndGuidance: true }));

    const guidancePromise = fetchApprovalsGuidanceV2(uid);
    const viewMetadataPromise = fetch(`/api/views/${fourByFour}.json`, fetchOptions)
      .then(checkStatus)
      .then((response) => response.json());

    try {
      const [view, fetchedApprovalsGuidance] = await Promise.all([viewMetadataPromise, guidancePromise]);

      setData((prev) => ({
        ...prev,
        fetchingViewAndGuidance: false,
        viewFetched: true,
        view,
        fetchedApprovalsGuidance,
      }));
    } catch (error) {
      console.error('Error fetching view or guidance', error);
      airbrake.notify({ error });
      setData((prev) => ({
        ...prev,
        failedViewFetch: true,
        fetchingViewAndGuidance: false,
      }));
    }
  }, [uid]);

  const fetchDataIfNeeded = useCallback(() => {
    const { failedViewFetch, viewFetched } = data;

    // NOTE: if this is federated, the only actions available are "Go to source",
    // which is available from the props already passed to this, so we do not
    // fetch guidance or the view object.
    if (
      !isCatalogFederatedView &&
      !isDataFederatedView &&
      viewFetched === false &&
      failedViewFetch === false
    ) {
      fetchViewAndGuidance();
    }
  }, [data, isCatalogFederatedView, isDataFederatedView, fetchViewAndGuidance]);

  const setNeedsViewUpdate = useCallback(() => {
    setData((prev) => ({ ...prev, viewFetched: false }));
  }, []);

  return {
    ...data,
    fetchDataIfNeeded,
    setNeedsViewUpdate,
  };
}
